import { type Client, ClientType } from '@maru44/huntre-utils/src/models/client'
import { type ClientMember, ClientMemberRole } from '@maru44/huntre-utils/src/models/clientMember'
import type { ClientShippingAddress } from '@maru44/huntre-utils/src/models/clientShippingAddress'
import type { ClientSubGroup } from '@maru44/huntre-utils/src/models/clientSubGroup'
import { setExtra, setUser } from '@sentry/react'
import { serverTimestamp, updateDoc } from 'firebase/firestore'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { useClientWithId } from 'src/hooks/background/useClient'
import { useClientShippingAddress } from 'src/hooks/background/useClientShippingAddress'
import { useClientSubGroups } from 'src/hooks/background/useClientSubGroups'
import { useUser } from 'src/hooks/background/useUser'
import type { ProSubscriptionStatus } from 'src/types/proSubscription'
import { NotAccessibleClientError } from 'src/utils/error'
import { useHttpsCallable } from 'src/utils/firebase'
import { capture } from 'src/utils/sentry'
import { AuthContext } from './AuthProvider'

type ClientState = {
  client: Client | undefined
  currentMember: ClientMember | undefined
  clientMembers: ClientMember[]
  clientShippingAddresses: ClientShippingAddress[]
  clientSubGroups: ClientSubGroup[]
  isLoading: boolean
  error: Error | undefined
  proSubscription: ProSubscriptionStatus | null | undefined
  isProSubscriptionLoading: boolean
  refreshProSubscription: () => Promise<void>
  selectClient: (clientId: string) => Promise<void>
  refreshClientSubGroups: () => Promise<void>
}

export const ClientContext = createContext<ClientState>({
  client: undefined,
  currentMember: undefined,
  clientMembers: [],
  clientShippingAddresses: [],
  clientSubGroups: [],
  isLoading: false,
  error: undefined,
  proSubscription: undefined,
  isProSubscriptionLoading: false,
  refreshProSubscription: async () => {},
  selectClient: async (_clientId: string) => {},
  refreshClientSubGroups: async () => {},
})

export function ClientProvider() {
  const [client, setClient] = useState<Client | undefined>()
  const [currentMember, setCurrentMember] = useState<ClientMember | undefined>()
  const [clientMembers, setClientMembers] = useState<ClientMember[]>([])
  const [clientShippingAddresses, setClientShippingAddresses] = useState<ClientShippingAddress[]>([])
  const [clientSubGroups, setClientSubGroups] = useState<ClientSubGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>()
  const [user, isLoading] = useContext(AuthContext)
  // const navigate = useNavigate()

  const { getClient, listClientMembers } = useClientWithId()
  const { getUser } = useUser()
  const { listShippingAddresses } = useClientShippingAddress()
  const { listClientSubGroups } = useClientSubGroups()

  const params = useParams()
  const selectClient = useCallback(
    async (clientId: string) => {
      setLoading(true)
      try {
        if (!user) {
          return
        }
        const fUser = await getUser(user.uid)
        if (!fUser) {
          setError(new Error('user not found'))
        }

        // set sentry scope
        setUser({ id: user.uid, email: user.email ?? undefined, username: user.displayName ?? undefined })

        const client = await getClient(clientId)
        await updateDoc(fUser.ref, { selectedClient: clientId, updatedAt: serverTimestamp() })
        setClient(client ?? undefined)
        setExtra('clientId', client.id)

        const members = await listClientMembers(clientId)
        setClientMembers(members)
        setCurrentMember(members.find((v) => v.id === user.uid))

        const addresses = await listShippingAddresses(clientId)
        setClientShippingAddresses(addresses)

        const subGroups = await listClientSubGroups(clientId)
        setClientSubGroups(subGroups)
      } catch (e) {
        if (e instanceof Error) {
          setError(e)
        } else {
          setError(new Error('unkonwn error with fetch client'))
        }
      } finally {
        setLoading(false)
      }
    },
    [
      user,
      setLoading,
      getClient,
      setError,
      getUser,
      listClientMembers,
      setClientMembers,
      listShippingAddresses,
      setClientShippingAddresses,
      setCurrentMember,
      listClientSubGroups,
    ],
  )

  useEffect(() => {
    const fetchClient = async () => {
      if (isLoading) {
        return
      }
      // ログインしてない
      if (!user) {
        setLoading(false)
        return
      }

      try {
        const fUser = await getUser(user.uid)

        // set sentry scope
        setUser({ id: user.uid, email: user.email ?? undefined, username: user.displayName ?? undefined })

        const clientId = params.clientId ?? fUser?.data?.selectedClient ?? ''
        const client = await getClient(clientId)
        const members = await listClientMembers(clientId)
        const current = members.find((v) => v.id === user.uid)
        setCurrentMember(current ?? undefined)

        // clientに所属してなかったら 404
        if (!current) {
          setLoading(false)
          setError(new NotAccessibleClientError('the member not in client', clientId))
          return
        }

        await updateDoc(fUser.ref, { selectedClient: clientId, updatedAt: serverTimestamp() })
        setClient(client)
        setExtra('clientId', clientId)

        setClientMembers(members)

        const addresses = await listShippingAddresses(clientId)
        setClientShippingAddresses(addresses)

        const subGroups = await listClientSubGroups(clientId)
        setClientSubGroups(subGroups)
      } catch (e) {
        if (e instanceof Error) {
          setError(e)
        } else {
          setError(new Error('unkonwn error with fetch client'))
        }
      } finally {
        setLoading(false)
      }
    }

    fetchClient()
  }, [
    user,
    isLoading,
    params.clientId,
    setLoading,
    getClient,
    getUser,
    setClient,
    setError,
    listClientMembers,
    setClientMembers,
    listShippingAddresses,
    setClientShippingAddresses,
    setCurrentMember,
    listClientSubGroups,
  ])

  const refreshClientSubGroups = useCallback(async () => {
    if (!client?.id) return
    const subGroups = await listClientSubGroups(client.id)
    setClientSubGroups(subGroups)
  }, [client?.id, listClientSubGroups])

  const [proSubscription, setProSubscription] = useState<ProSubscriptionStatus | null | undefined>(undefined)
  const [isProSubscriptionLoading, setIsProSubscriptionLoading] = useState(false)
  const [callGetProSub] = useHttpsCallable<{ clientId: string }, { subscription: ProSubscriptionStatus | null }>('getProSubscriptionStatus')

  const refreshProSubscription = useCallback(async () => {
    if (!client?.id) {
      setProSubscription(undefined)
      return
    }
    // admin だけが Stripe を見て真偽を判定できる (callable が admin 限定)
    // Firestore の plan ではなく Stripe の状態を真実とするため、Free でも呼ぶ
    if (currentMember?.role !== ClientMemberRole.Admin) {
      setProSubscription(null)
      return
    }
    // Pro プランは group クライアントのみ対象
    if (client.type !== ClientType.Group) {
      setProSubscription(null)
      return
    }
    setIsProSubscriptionLoading(true)
    try {
      const res = await callGetProSub({ clientId: client.id })
      setProSubscription(res?.data?.subscription ?? null)
    } catch (e) {
      capture(e)
      setProSubscription(null)
    } finally {
      setIsProSubscriptionLoading(false)
    }
  }, [client?.id, client?.type, currentMember?.role, callGetProSub])

  useEffect(() => {
    refreshProSubscription()
  }, [refreshProSubscription])

  const ctx = useMemo(
    () => ({
      client,
      currentMember,
      clientMembers,
      clientShippingAddresses,
      clientSubGroups,
      isLoading: loading,
      error,
      proSubscription,
      isProSubscriptionLoading,
      refreshProSubscription,
      selectClient,
      refreshClientSubGroups,
    }),
    [
      client,
      clientMembers,
      clientShippingAddresses,
      clientSubGroups,
      loading,
      error,
      proSubscription,
      isProSubscriptionLoading,
      refreshProSubscription,
      selectClient,
      refreshClientSubGroups,
    ],
  )

  return (
    <ClientContext.Provider value={ctx}>
      <Outlet />
    </ClientContext.Provider>
  )
}
